import { ApiResponse, HttpHeader, MimeValueType, ResponseWithHeaders } from '@cs-cms/types';
import { HTTPError, Options, ResponsePromise } from 'ky';
import streamSaver from 'streamsaver';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export const executeHttpMethod = (
  client: any,
  httpMethod: HttpMethod,
  endpoint: string,
  options?: Options
): ResponsePromise => {
  const methodMap = {
    GET: () => client.get(endpoint, options),
    POST: () => client.post(endpoint, options),
    PUT: () => client.put(endpoint, options),
    DELETE: () => client.delete(endpoint, options),
    PATCH: () => client.patch(endpoint, options)
  };

  const method = methodMap[httpMethod] || methodMap.GET;
  return method();
};

export const processMsGraphResponse = (apiResponse: any): any => {
  if (apiResponse.error) {
    throw new Error(apiResponse.error?.message);
  }
  return apiResponse;
};

export const processGearApiResponse = (apiResponse: any): any => {
  if (Object.prototype.hasOwnProperty.call(apiResponse, 'isValid')) {
    const gearApiResponse = apiResponse as ApiResponse;

    if (gearApiResponse.isValid) {
      return apiResponse.result ?? null;
    }

    if (gearApiResponse.errorMessage) {
      throw new Error(gearApiResponse.errorMessage);
    }

    throw new Error('Invalid API response');
  }

  return apiResponse;
};

export const shouldReturnNull = (response: Response): boolean => {
  if (response.status === 204) {
    return true;
  }

  const contentType = response.headers.get(HttpHeader.ContentType);
  return !contentType || !contentType.includes(MimeValueType.Json);
};

export const parseSuccessResponse = async <TResult>(kyResponse: Response, isMsGraphApi: boolean): Promise<TResult> => {
  if (shouldReturnNull(kyResponse)) {
    return null as TResult;
  }

  const apiResponse: any = await kyResponse.json();
  return isMsGraphApi ? processMsGraphResponse(apiResponse) : processGearApiResponse(apiResponse);
};

export const hasJsonContent = (response: Response): boolean => {
  const contentType = response.headers.get(HttpHeader.ContentType);
  return contentType !== null && contentType.includes(MimeValueType.Json);
};

export const parseErrorResponse = async (errorResponse: Response, isMsGraphApi: boolean): Promise<void> => {
  if (!hasJsonContent(errorResponse)) {
    return;
  }

  const errorData = await errorResponse.json();

  if (isMsGraphApi) {
    processMsGraphResponse(errorData);
  } else {
    processGearApiResponse(errorData);
  }
};

export const attachErrorMetadata = (parseError: Error, statusCode: number, response: Response): Error => {
  (parseError as any).status = statusCode;
  (parseError as any).response = response;
  return parseError;
};

export const handleHTTPError = async (error: HTTPError, isMsGraphApi: boolean): Promise<void> => {
  const statusCode = error.response.status;

  try {
    await parseErrorResponse(error.response, isMsGraphApi);
  } catch (parseError) {
    if (parseError instanceof Error && parseError.message !== error.message) {
      throw attachErrorMetadata(parseError, statusCode, error.response);
    }
  }
};

export const extractEtag = (response: Response): string | undefined => {
  return response.headers.get('etag') ?? undefined;
};

export const parseSuccessResponseWithHeaders = async <TResult>(
  kyResponse: Response,
  isMsGraphApi: boolean
): Promise<ResponseWithHeaders<TResult>> => {
  const etag = extractEtag(kyResponse);

  if (shouldReturnNull(kyResponse)) {
    return {
      data: null as TResult,
      headers: { etag }
    };
  }

  const apiResponse: any = await kyResponse.json();
  const processedData = isMsGraphApi ? processMsGraphResponse(apiResponse) : processGearApiResponse(apiResponse);

  return {
    data: processedData,
    headers: { etag }
  };
};

export const extractFilenameFromHeader = (contentDisposition: string | null, defaultFilename: string): string => {
  if (!contentDisposition) {
    return defaultFilename;
  }

  const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);

  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1].replace(/['"]/g, '');
  }

  return defaultFilename;
};

export const streamResponseToFile = async (
  responseBody: ReadableStream<Uint8Array>,
  filename: string
): Promise<void> => {
  const fileStream = streamSaver.createWriteStream(filename);
  const writer = fileStream.getWriter();
  const reader = responseBody.getReader();

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      await writer.write(value);
    }
  } finally {
    writer.close();
  }
};
